import { useCallback, useContext } from 'react'
import { useMedplum } from '@medplum/react'
import dayjs from 'dayjs'
import { QuestionnaireResponse } from '@medplum/fhirtypes'
import { UpdateQuestionnaireResponseMutation } from 'generated/graphql'
import { rebuildResponseFromFlat } from 'components/Patients/Assessments/CreateAssessment/helpers'
import AssessmentLayoutContext from 'contexts/AssessmentLayoutContext'
import { getGraphQLErrorMessage, removeNullValues } from 'utils/helpers'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { NotificationContext } from 'contexts/NotificationContext'
import { flattenQuestionnaireResponseItem } from 'components/Patients/Assessments/CreateAssessment/helpers'
import PatientContext from 'contexts/PatientContext'
import { useStore } from 'store'
import { AdapterRegistry } from '../adapters/AdapterRegistry'
import { UPDATE_QUESTIONNAIRE_RESPONSE } from '../updateQuestionnaireResponse.mutation'
import { AdapterError } from '../adapters/types'

interface UpdateAssessmentUncontrolledParams {
  formValues: Record<string, unknown>
  response: QuestionnaireResponse | null
}

export type UpdateAssessmentUncontrolledFunction = ({
  formValues,
  response
}: UpdateAssessmentUncontrolledParams) => Promise<void>

export const useUpdateAssessmentUncontrolled = (): UpdateAssessmentUncontrolledFunction => {
  const { cantEditAssessmentFields, assessmentType } = useAssessmentContext()
  const { notify } = useContext(NotificationContext)
  const { setAutosaveDate } = useContext(AssessmentLayoutContext)
  const medplum = useMedplum()

  // Get caregiver timezone for timezone conversions in adapter
  const { patientId } = useContext(PatientContext)
  const { idToPatientMapping } = useStore((state) => state.patient)
  const caregiverTimezone = patientId ? idToPatientMapping[patientId]?.caregiverTimezone : undefined

  const updateAssessmentUncontrolled: UpdateAssessmentUncontrolledFunction = useCallback(
    async ({ formValues, response }) => {
      if (cantEditAssessmentFields || !response || !assessmentType) return

      try {
        // Convert form values to flatAnswers format using adapter pattern
        const adapter = AdapterRegistry.getAdapter(assessmentType)
        const updatedFhir = adapter.toFHIR(formValues, response, {
          caregiverTimezone: caregiverTimezone ?? ''
        })
        const updatedFlatAnswers = flattenQuestionnaireResponseItem(updatedFhir.item ?? [])
        // Transform flatAnswers into questionnaire response
        let answers
        try {
          answers = rebuildResponseFromFlat(updatedFlatAnswers)
        } catch (err) {
          throw new Error('Rebuilding flatAnswers failed during uncontrolled auto save')
        }
        // Build valid QuestionnaireResponse (answers cannot be null)
        const resource = {
          ...response,
          authored: dayjs().toISOString(),
          item: removeNullValues(answers)
        }

        const {
          data,
          errors
        }: { data: UpdateQuestionnaireResponseMutation; errors: [Record<string, unknown>] } =
          await medplum.graphql(UPDATE_QUESTIONNAIRE_RESPONSE, null, {
            responseId: resource.id,
            resource
          })

        if (errors) {
          const errorMessage = getGraphQLErrorMessage(errors)
          notify.warning({
            message: 'Failed to update assessment',
            description: errorMessage
          })
        }

        if (data.QuestionnaireResponseUpdate?.authored) {
          setAutosaveDate(dayjs(data.QuestionnaireResponseUpdate?.authored).toDate())
        }
      } catch (error) {
        notify.error({
          message: 'Auto-save failed',
          description: 'There was an error saving your form changes automatically.'
        })
        if (error instanceof AdapterError) {
          throw error
        }
      }
    },
    [cantEditAssessmentFields, assessmentType, caregiverTimezone, medplum, notify, setAutosaveDate]
  )

  return updateAssessmentUncontrolled
}
